import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom'
import { WagmiProvider } from 'wagmi'
import { AdminShell } from '@/layout/AdminShell'
import { ApiError } from '@/lib/api'
import { SessionProvider } from '@/lib/session'
import { wagmiConfig } from '@/lib/wagmi'
import { KillSwitch } from '@/pages/KillSwitch'
import { Ops } from '@/pages/Ops'
import { Withdrawals } from '@/pages/Withdrawals'
import { ErrorState } from '@/ui/primitives'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // A 4xx is an answer, not a transient failure — retrying just burns rate limit.
        // It matters more here: a 403 means this wallet is not an operator, and hammering
        // the endpoint three times will not change that.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false
        return failureCount < 2
      },
      refetchOnWindowFocus: true,
      staleTime: 5_000,
    },
  },
})

/*
 * No route-level code splitting.
 *
 * The public app lazy-loads every page to hold a 200 KB entry budget for first-time
 * visitors on a phone. This app has three pages, no charts, no wallet-heavy routes, and
 * a user population of a handful of operators who load it deliberately. Splitting it
 * would trade a suspense boundary and a loading flash for a saving nobody here is
 * waiting on.
 */
const router = createBrowserRouter([
  {
    path: '/',
    element: <AdminShell />,
    errorElement: (
      <div className="grid min-h-dvh place-items-center">
        <ErrorState
          message="Something went wrong loading this page."
          retry={() => window.location.reload()}
        />
      </div>
    ),
    children: [
      { index: true, element: <Ops /> },
      { path: 'withdrawals', element: <Withdrawals /> },
      { path: 'kill-switch', element: <KillSwitch /> },
      // Anything else lands on the board rather than a 404 — there is nowhere else to be.
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
])

export function App() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        {/*
         * SessionProvider sits ABOVE the router, not inside it.
         *
         * It owns the in-memory access token and the rotation timer, so it must survive
         * navigation. Mounted per-route it would remount on every link click, dropping
         * the token and forcing a silent refresh each time — which looks exactly like an
         * app that logs you out at random.
         */}
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}
